import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import ModernDialogHost from './nyafaz-ep/admin-shared/ModernDialogHost';
import { SkinSessionProvider as SkinFlowSessionProvider } from './skin/context/SkinSessionContext';
import { SkinAnalysisPlanProvider as SkinFlowPlanProvider } from './skin/context/SkinAnalysisPlanContext';
import { PageBackgroundSync as SkinPageBackgroundSync } from './skin/components/PageBackgroundSync';
import SkinLandingPage from './skin/pages/LandingPage';
import SkinUserInfoPage from './skin/pages/UserInfoPage';
import SkinFaceDetectionPage from './skin/pages/FaceDetectionPage';
import SkinAISkinAnalysisPage from './skin/pages/AISkinAnalysisPage';
import SkinSkinRoutinePage from './skin/pages/SkinRoutinePage';
import SkinProductRecommendationsPage from './skin/pages/ProductRecommendationsPage';
import SkinResultPage from './skin/pages/SkinResultPage';
import { SkinSessionProvider as SalonSessionProvider } from './salon/context/SkinSessionContext';
import { SkinAnalysisPlanProvider as SalonPlanProvider } from './salon/context/SkinAnalysisPlanContext';
import { PageBackgroundSync as SalonPageBackgroundSync } from './salon/components/PageBackgroundSync';
import SalonLoginPage from './salon/pages/LoginPage';
import './salon/styles/SalonShell.css';
import SalonFlowSelectPage from './salon/pages/FlowSelectPage';
import SalonLandingPage from './salon/pages/LandingPage';
import SalonUserInfoPage from './salon/pages/UserInfoPage';
import SalonFaceDetectionPage from './salon/pages/FaceDetectionPage';
import SalonAISkinAnalysisPage from './salon/pages/AISkinAnalysisPage';
import SalonSkinRoutinePage from './salon/pages/SkinRoutinePage';
import SalonProductRecommendationsPage from './salon/pages/ProductRecommendationsPage';
import SalonTreatmentRecommendationsPage from './salon/pages/TreatmentRecommendationsPage';
import SalonAppointmentBookingPage from './salon/pages/AppointmentBookingPage';
import SalonResultPage from './salon/pages/SalonResultPage';
import {
  clearSalonAuthStorage,
  hasActiveSalonLogin,
  ensureSalonLoginValidated,
  shouldClearSalonSessionOnValidateFailure,
  clearSalonLoginServerValidated,
} from './salon/utils/salonAuth';
import SalonAuthSessionWatcher from './salon/components/SalonAuthSessionWatcher';
import BrandAdminFlowLayout from './brand-admin/BrandAdminFlowLayout';
import { renderBrandAdminRoutes } from './brand-admin/App';
import SalonAdminFlowLayout from './salon-admin/SalonAdminFlowLayout';
import { renderSalonAdminRoutes } from './salon-admin/App';
import NaturalsExecutiveFlowLayout from './naturals/executive/SalonAdminFlowLayout';
import { renderExecutiveRoutes as renderNaturalsExecutiveRoutes } from './naturals/executive/App';
import NaturalsWorkspaceFlowLayout from './naturals/workspace/SalonAdminFlowLayout';
import { renderWorkspaceRoutes as renderNaturalsWorkspaceRoutes } from './naturals/workspace/App';
import NaturalsPartnerFlowLayout from './naturals/partner/SalonAdminFlowLayout';
import { renderPartnerRoutes as renderNaturalsPartnerRoutes } from './naturals/partner/App';
import { renderSalonAdminLegacyRedirects as renderNaturalsSalonAdminLegacyRedirects } from './naturals/salon-admin/App';
import { renderNyafazPortalRoutes } from './nyafaz-portal/App';
import { renderEnterprisePortalRoutes } from './enterprise-portal/App';
import { renderNyafazEpAdminRoutes } from './nyafaz-ep-admin/App';
import NyafazEpExecutiveFlowLayout from './nyafaz-ep/executive/SalonAdminFlowLayout';
import { renderExecutiveRoutes as renderNyafazEpExecutiveRoutes } from './nyafaz-ep/executive/App';
import NyafazEpWorkspaceFlowLayout from './nyafaz-ep/workspace/SalonAdminFlowLayout';
import { renderWorkspaceRoutes as renderNyafazEpWorkspaceRoutes } from './nyafaz-ep/workspace/App';
import NyafazEpPartnerFlowLayout from './nyafaz-ep/partner/SalonAdminFlowLayout';
import { renderPartnerRoutes as renderNyafazEpPartnerRoutes } from './nyafaz-ep/partner/App';
import { renderSalonAdminLegacyRedirects as renderNyafazEpSalonAdminLegacyRedirects } from './nyafaz-ep/salon-admin/App';
import NyafazEpPortalLogin from './nyafaz-ep/workspace/pages/NyafazEpPortalLogin';
import { renderNyafazEpAppRoutes } from './nyafaz-ep/renderNyafazEpAppRoutes';
import { renderNaturalsAdminRoutes } from './naturals-admin/App';
import NaturalsPortalLogin from './naturals/workspace/pages/NaturalsPortalLogin';
import { renderNaturalsAppRoutes } from './naturals/renderNaturalsAppRoutes';
import { renderNaturalsPortalRoutes } from './naturals-portal/App';
import NaturalsEngagementApp from './naturals/engagement/EngagementApp';
import NyafazEngagementApp from './nyafaz-ep/nyafaz-engagement/NyafazEngagementApp';
import DemoPosApp from './nyafaz-ep/demo-pos/DemoPosApp';
import SampleProductsApp from './sample-products/SampleProductsApp';
import SampleProductsHomePage from './sample-products/pages/HomePage';
import SampleProductsPage from './sample-products/pages/ProductsPage';
import SampleProductDetailPage from './sample-products/pages/ProductDetailPage';
import SampleProductsCartPage from './sample-products/pages/CartPage';
import SampleProductsCartLinkPage from './sample-products/pages/CartLinkPage';
import SampleProductsCheckoutPage from './sample-products/pages/CheckoutPage';
import HairFlowLayout from './hair-analyzer/src/hair/HairFlowLayout';
import { renderHairAnalyzerRoutes } from './hair-analyzer/src/hair/renderHairAnalyzerRoutes';
import { MakeupHairLegacyRedirect } from './beauty-shared/components/BeautyLegacyRedirects';
import FluxPromptSampleApp from './flux-prompt-sample/FluxPromptSampleApp';
import FluxReferenceTransferApp from './flux-reference-transfer/FluxReferenceTransferApp';
import NseCelebrationApp from './nse-celebration/NseCelebrationApp';
import './App.css';

const isBrandAdminTarget = process.env.REACT_APP_TARGET === 'brand-admin';
const isSalonAdminTarget = process.env.REACT_APP_TARGET === 'salon-admin';
const isNyafazPortalTarget = process.env.REACT_APP_TARGET === 'nyafaz-portal';
const isNaturalsPortalTarget = process.env.REACT_APP_TARGET === 'naturals-portal';
const isNaturalsSalonAdminTarget = process.env.REACT_APP_TARGET === 'naturals-salon-admin';
const isNaturalsFranchiseAdminTarget = process.env.REACT_APP_TARGET === 'naturals-franchise-admin';
const isNaturalsExecutiveTarget = process.env.REACT_APP_TARGET === 'naturals-executive';
const isNaturalsEngagementTarget =
  process.env.REACT_APP_TARGET === 'engagement' || process.env.REACT_APP_TARGET === 'demo-engagement';
const isNyafazEngagementTarget =
  process.env.REACT_APP_TARGET === 'nyafaz-engagement' ||
  process.env.REACT_APP_TARGET === 'enterprise-engagement';
const isDemoPosTarget = process.env.REACT_APP_TARGET === 'demo-pos';
const isDedicatedAdminTarget =
  isBrandAdminTarget ||
  isSalonAdminTarget ||
  isNyafazPortalTarget ||
  isNaturalsPortalTarget ||
  isNaturalsSalonAdminTarget ||
  isNaturalsFranchiseAdminTarget ||
  isNaturalsExecutiveTarget;

function isAnalyzeNyafazHost() {
  try {
    return String(window.location.hostname || '').toLowerCase() === 'analyze.nyafaz.com';
  } catch {
    return false;
  }
}

const showSkinRoutesAtRoot = !isDedicatedAdminTarget || isAnalyzeNyafazHost();

function SalonAuthRetryScreen({ message, onRetry }) {
  return (
    <div style={{ padding: '2rem', textAlign: 'center', maxWidth: 420, margin: '4rem auto' }}>
      <h2 style={{ marginBottom: '0.75rem' }}>Connection issue</h2>
      <p style={{ marginBottom: '1.25rem', color: '#555' }}>
        {message || 'Could not verify your salon login. Check your network and try again.'}
      </p>
      <button type="button" className="skin-action-button" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

function createSalonProtectedRoute({
  hasActiveLogin,
  ensureValidated,
  shouldClearOnFailure,
  clearAuthStorage,
  clearServerValidated,
  loginPath,
}) {
  return function SalonProtectedRoute() {
    const location = useLocation();
    const [gate, setGate] = useState('checking');
    const [retryMessage, setRetryMessage] = useState('');

    const runVerify = useCallback(async () => {
      if (!hasActiveLogin()) {
        setGate('denied');
        return;
      }

      const result = await ensureValidated();
      if (result.ok) {
        setGate('allowed');
        setRetryMessage('');
        return;
      }

      if (shouldClearOnFailure(result)) {
        clearAuthStorage();
        setGate('denied');
        return;
      }

      setRetryMessage(result.error || 'Could not verify your salon login.');
      setGate('retry');
    }, []);

    useEffect(() => {
      let cancelled = false;

      async function verify() {
        setGate('checking');
        if (!hasActiveLogin()) {
          if (!cancelled) setGate('denied');
          return;
        }

        const result = await ensureValidated();
        if (cancelled) return;

        if (result.ok) {
          setGate('allowed');
          return;
        }

        if (shouldClearOnFailure(result)) {
          clearAuthStorage();
          setGate('denied');
          return;
        }

        setRetryMessage(result.error || 'Could not verify your salon login.');
        setGate('retry');
      }

      verify();
      return () => {
        cancelled = true;
      };
    }, [location.pathname]);

    const handleRetry = useCallback(async () => {
      setGate('checking');
      clearServerValidated();
      await runVerify();
    }, [runVerify]);

    if (gate === 'checking') return null;
    if (gate === 'denied') {
      return <Navigate to={loginPath} replace state={{ from: location.pathname }} />;
    }
    if (gate === 'retry') {
      return <SalonAuthRetryScreen message={retryMessage} onRetry={handleRetry} />;
    }
    return <Outlet />;
  };
}

const SalonProtectedRoute = createSalonProtectedRoute({
  hasActiveLogin: hasActiveSalonLogin,
  ensureValidated: ensureSalonLoginValidated,
  shouldClearOnFailure: shouldClearSalonSessionOnValidateFailure,
  clearAuthStorage: clearSalonAuthStorage,
  clearServerValidated: clearSalonLoginServerValidated,
  loginPath: '/salon/login',
});

function SkinFlowLayout() {
  return (
    <div className="skin-flow-shell">
      <SkinFlowSessionProvider>
        <SkinFlowPlanProvider>
          <SkinPageBackgroundSync />
          <Outlet />
        </SkinFlowPlanProvider>
      </SkinFlowSessionProvider>
    </div>
  );
}

function SalonFlowLayout() {
  return (
    <div className="ny-salon-shell">
      <SalonSessionProvider>
        <SalonPlanProvider>
          <SalonAuthSessionWatcher />
          <SalonPageBackgroundSync />
          <Outlet />
          <ModernDialogHost />
          <div id="ny-salon-modal-root" />
        </SalonPlanProvider>
      </SalonSessionProvider>
    </div>
  );
}

/** Legacy /enterprise/* → Nyafaz EP equivalents (enterprise kiosk removed from this SPA). */
function EnterpriseLegacyRedirect() {
  const location = useLocation();
  const path = location.pathname || '';
  const search = location.search || '';

  if (path.startsWith('/enterprise/salon') || path.startsWith('/enterprise/passport') || path.startsWith('/enterprise/face-identity') || path.startsWith('/enterprise/guest-checkin')) {
    const sub = path.replace(/^\/enterprise\/(?:salon|passport|face-identity|guest-checkin)\/?/, '');
    const tail = sub ? `/${sub}` : '';
    return <Navigate to={`/nyafaz-ep/salon${tail}${search}`} replace state={location.state} />;
  }
  if (path.startsWith('/enterprise/makeup') || path.startsWith('/enterprise/looks') || path.startsWith('/enterprise/makeup-studio-test')) {
    return <Navigate to={`/nyafaz-ep/makeup/capture${search}`} replace />;
  }
  if (path.startsWith('/enterprise/hair-coloring-test') || path.startsWith('/enterprise/hair-coloring')) {
    return <Navigate to={`/nyafaz-ep/hair-coloring/capture${search}`} replace />;
  }
  if (path.startsWith('/enterprise/hair-styling') || path.startsWith('/enterprise/haircut')) {
    return <Navigate to={`/nyafaz-ep/hair-styling/capture${search}`} replace />;
  }
  if (path.startsWith('/enterprise/hair-color')) {
    return <Navigate to={`/nyafaz-ep/hair-coloring/capture${search}`} replace />;
  }
  if (path.startsWith('/enterprise/presentation') || path.startsWith('/enterprise/reels') || path.startsWith('/enterprise/video')) {
    return <Navigate to={`/nyafaz-ep/presentation${search}`} replace />;
  }
  if (path.startsWith('/enterprise/engagement') || path.startsWith('/enterprise/demo-engagement') || path.startsWith('/enterprise/nyafaz-engagement')) {
    return <Navigate to={`/nyafaz-ep/engagement${search}`} replace />;
  }
  if (path.startsWith('/enterprise/demo-pos')) {
    return <Navigate to={`/nyafaz-ep/demo-pos${search}`} replace />;
  }
  if (path.startsWith('/enterprise/flux-prompt-sample')) {
    return <Navigate to="/flux-prompt-sample" replace />;
  }
  if (path.startsWith('/enterprise/flux-reference-transfer')) {
    return <Navigate to="/flux-reference-transfer" replace />;
  }
  if (path.startsWith('/enterprise/workspace') || path.startsWith('/enterprise/partner') || path.startsWith('/enterprise/executive') || path === '/enterprise/login') {
    return <Navigate to={`/nyafaz-ep/login${search}`} replace state={location.state} />;
  }
  return <Navigate to={`/nyafaz-ep/salon/login${search}`} replace />;
}

function App() {
  if (isNaturalsEngagementTarget) {
    return <NaturalsEngagementApp />;
  }
  if (isNyafazEngagementTarget) {
    return <NyafazEngagementApp />;
  }
  if (isDemoPosTarget) {
    return <DemoPosApp />;
  }
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <div className="App">
        <Routes>
          <Route path="/enterprise/*" element={<EnterpriseLegacyRedirect />} />
          <Route path="/enterprise" element={<EnterpriseLegacyRedirect />} />

          <Route path="/naturals/login" element={<NaturalsPortalLogin />} />
          <Route path="/nyafaz-ep/login" element={<NyafazEpPortalLogin />} />
          <Route element={<BrandAdminFlowLayout />}>
            {renderBrandAdminRoutes({ brandAdminHome: isBrandAdminTarget })}
          </Route>

          <Route element={<SalonAdminFlowLayout />}>
            {renderSalonAdminRoutes({ salonAdminHome: isSalonAdminTarget })}
          </Route>

          <Route element={<NaturalsExecutiveFlowLayout />}>
            {renderNaturalsExecutiveRoutes({ executiveHome: isNaturalsExecutiveTarget })}
          </Route>

          <Route element={<NaturalsWorkspaceFlowLayout />}>
            {renderNaturalsWorkspaceRoutes({
              workspaceHome: isNaturalsSalonAdminTarget && !isAnalyzeNyafazHost(),
            })}
          </Route>

          <Route element={<NaturalsPartnerFlowLayout />}>
            {renderNaturalsPartnerRoutes({ partnerHome: isNaturalsFranchiseAdminTarget })}
          </Route>

          {renderNaturalsSalonAdminLegacyRedirects()}

          {renderNyafazPortalRoutes({ portalHome: isNyafazPortalTarget })}
          {renderNaturalsPortalRoutes({ portalHome: isNaturalsPortalTarget })}
          {renderEnterprisePortalRoutes()}
          {renderNyafazEpAdminRoutes()}

          <Route element={<NyafazEpExecutiveFlowLayout />}>
            {renderNyafazEpExecutiveRoutes()}
          </Route>
          <Route element={<NyafazEpWorkspaceFlowLayout />}>
            {renderNyafazEpWorkspaceRoutes({ workspaceHome: false })}
          </Route>
          <Route element={<NyafazEpPartnerFlowLayout />}>
            {renderNyafazEpPartnerRoutes()}
          </Route>
          {renderNyafazEpSalonAdminLegacyRedirects()}
          {renderNyafazEpAppRoutes()}
          {renderNaturalsAdminRoutes()}
          {renderNaturalsAppRoutes()}

          <Route path="/sample-products" element={<SampleProductsApp />}>
            <Route index element={<SampleProductsHomePage />} />
            <Route path="products" element={<SampleProductsPage />} />
            <Route path="products/:slug" element={<SampleProductDetailPage />} />
            <Route path="cart" element={<SampleProductsCartPage />} />
            <Route path="cart/:cartString" element={<SampleProductsCartLinkPage />} />
            <Route path="checkout" element={<SampleProductsCheckoutPage />} />
          </Route>

          <Route path="/SalonResultPage" element={<SalonResultPage />} />
          <Route path="/salon/result" element={<SalonResultPage />} />
          <Route path="/salon/*" element={<Navigate to="/salon" replace />} />

          <Route path="/makeup/*" element={<Navigate to="/nyafaz-ep/makeup/capture" replace />} />
          <Route path="/hair-coloring/*" element={<Navigate to="/nyafaz-ep/hair-coloring/capture" replace />} />
          <Route path="/haircut/*" element={<Navigate to="/nyafaz-ep/hair-styling/capture" replace />} />
          <Route
            path="/makeup-hair/*"
            element={<MakeupHairLegacyRedirect toBase="/nyafaz-ep/makeup" />}
          />
          <Route path="/hairstyle/*" element={<Navigate to="/nyafaz-ep/hair-styling/capture" replace />} />

          <Route path="/hair" element={<Navigate to="/nyafaz-ep/hair" replace />} />

          <Route path="/test/hair-coloring" element={<Navigate to="/nyafaz-ep/hair-coloring/capture" replace />} />
          <Route path="/test/beauty" element={<Navigate to="/nyafaz-ep/salon/test" replace />} />

          <Route path="/flux-prompt-sample" element={<FluxPromptSampleApp />} />

          <Route path="/flux-reference-transfer" element={<FluxReferenceTransferApp />} />
          <Route path="/nse-celebration" element={<NseCelebrationApp />} />

          <Route path="/salon" element={<SalonFlowLayout />}>
            <Route index element={<SalonLoginPage />} />
            <Route path="login" element={<SalonLoginPage />} />
            <Route element={<SalonProtectedRoute />}>
              <Route path="hub" element={<SalonFlowSelectPage />} />
              <Route path="landing" element={<SalonLandingPage />} />
              <Route path="user-info" element={<SalonUserInfoPage />} />
              <Route path="capture" element={<SalonFaceDetectionPage />} />
              <Route path="skin-analysis" element={<SalonAISkinAnalysisPage />} />
              <Route path="skin-routine" element={<SalonSkinRoutinePage />} />
              <Route path="product-recommendations" element={<SalonProductRecommendationsPage />} />
              <Route path="treatment-recommendations" element={<SalonTreatmentRecommendationsPage />} />
              <Route path="appointment" element={<SalonAppointmentBookingPage />} />
            </Route>
          </Route>

          {showSkinRoutesAtRoot ? (
            <Route element={<SkinFlowLayout />}>
              <Route path="/" element={<SkinLandingPage />} />
              <Route path="/session/:token" element={<SkinLandingPage />} />
              <Route path="/user-info" element={<SkinUserInfoPage />} />
              <Route path="/capture" element={<SkinFaceDetectionPage />} />
              <Route path="/skin-analysis" element={<SkinAISkinAnalysisPage />} />
              <Route path="/skin-routine" element={<SkinSkinRoutinePage />} />
              <Route path="/product-recommendations" element={<SkinProductRecommendationsPage />} />
              <Route path="/skin-result" element={<SkinResultPage />} />
            </Route>
          ) : null}
        </Routes>
      </div>
    </Router>
  );
}

export default App;
